import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { 
  FaBox, FaPlus, FaEye, FaUserTie, FaCalendarAlt,
  FaMapMarkerAlt, FaWeightHanging, FaComments,
  FaCheckCircle, FaClock, FaExclamationTriangle,
  FaBuilding, FaArrowRight, FaEdit, FaTrash,
  FaTruck, FaTimes, FaSave, FaUserCircle,
  FaDolly, FaUsers, FaSearch, FaFilter,
  FaInfoCircle, FaMoneyBillWave, FaCalculator,
  FaFileAlt, FaEnvelope, FaPhone, FaUser,
  FaList, FaCheck, FaSpinner
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { jobs, clients, subcontractors } from '../api';
import InteractiveRouteMap from '../components/InteractiveRouteMap';
import ProformaInvoiceButton from '../components/ProformaInvoiceButton';
import PurchaseOrderButton from '../components/PurchaseOrderButton';
import ConfirmDialog from '../components/ConfirmDialog';

// ============================================
// GOOGLE MAPS - shared loader (hooks/useGoogleMaps)
// ============================================
function NewJobs({ onLogout }) {
  const { isLoaded } = useGoogleMaps();

  const [jobsList, setJobsList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [subbiesList, setSubbiesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [allocations, setAllocations] = useState({});
  const [lineItemsByJob, setLineItemsByJob] = useState({});
  // Record completed work (off-system) modal
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordJob, setRecordJob] = useState(null);
  const [recordDrafts, setRecordDrafts] = useState({});
  const [recordingId, setRecordingId] = useState(null);

  // ✅ Allocation Detail Modal state
  const [showAllocationDetailModal, setShowAllocationDetailModal] = useState(false);
  const [selectedJobForAllocationDetail, setSelectedJobForAllocationDetail] = useState(null);

  // ✅ RFQ state
  const [isRFQ, setIsRFQ] = useState(false);
  
  // ✅ Quote Type state (for jobs created from quotes)
  const [jobType, setJobType] = useState('minerals');

  // Create Job form state
  const [jobForm, setJobForm] = useState({
    client_id: '',
    commodity: '',
    commodity_type: 'mining',
    total_quantity_mt: '',
    rate_per_mt: '',
    subbie_target_price: '',
    admin_quote_to_client: '',
    load_size_per_truck: '',
    loadcon_file: null,
    loadcon_ref_number: '',
    origin: '',
    destination: '',
    origin_country: '',
    destination_country: '',
    distance_km: '',
    start_date: '',
    estimated_end_date: '',
    priority: 'normal',
    notes: '',
    rfq_client_name: '',
    rfq_contact_person: '',
    rfq_contact_email: '',
    rfq_contact_phone: '',
    rfq_company_address: '',
    // ✅ NEW: Quote Type Specific Fields
    price_type: 'quoted',
    quote_type: 'minerals',
    wet_or_dry: 'dry',
    truck_type: 'side_tipper',
    truck_type_other: '',
    job_description: '',
    services: '',
    hourly_rate: '',
    estimated_hours: '',
    items: [],
    // ✅ PHASE 5: Container fields
    container_count: 1,
    container_size: '6m',
    container_type: 'dry',
    is_empty: true,
    cross_border: false,
    site_loading: false,
    repo_move: false,
    combined_weight_t: ''
  });
  const [allocating, setAllocating] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [allocateMode, setAllocateMode] = useState('whole'); // 'whole' | 'line_items'
  const [lineItemAllocations, setLineItemAllocations] = useState({}); // { [lineItemIdx]: subbieId }
  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const jobsData = await jobs.getAll();
      const pendingJobs = jobsData.jobs?.filter(j => j.status === 'pending' || j.status === 'new') || [];
      setJobsList(pendingJobs);
      
      const allocationsData = {};
      const lineItemsData = {};
      for (const job of pendingJobs) {
        const response = await fetch(`${API_URL}/job-requirements/job/${job.id}`);
        const data = await response.json();
        if (data.success) {
          allocationsData[job.id] = data.requirements || [];
        }
        // Per-line assignments live on job_line_items — include them so the card
        // shows a job as allocated when lines (not just whole-job) are assigned.
        const liResp = await fetch(`${API_URL}/job-line-items?job_id=${job.id}`);
        const liData = await liResp.json();
        if (liData.success) lineItemsData[job.id] = liData.line_items || [];
      }
      setAllocations(allocationsData);
      setLineItemsByJob(lineItemsData);
      
      const clientsData = await clients.getAll();
      setClientsList(clientsData.clients || []);
      
      const subbiesData = await subcontractors.getAll();
      setSubbiesList(subbiesData.subcontractors || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load new jobs.');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocate = (job) => {
    setSelectedJob(job);
    // 🔑 PRE-FILL every line that is ALREADY allocated (matched on line_number).
    // Before this, the modal opened with every dropdown blank, so after freeing one line the
    // user had no way to see which line was already done and was pushed into allocating it
    // again. Now the allocated lines appear with their subbie + dates already filled in and
    // marked "Allocated", so only the genuinely free lines need attention.
    const existing = (lineItemsByJob[job.id] || []).filter(li => li.subcontractor_id);
    const seedAlloc = {};
    const seedDates = {};
    existing.forEach(li => {
      const idx = (parseInt(li.line_number, 10) || 0) - 1;
      if (idx < 0) return;
      seedAlloc[idx] = li.subcontractor_id;
      seedDates[idx] = {
        pickup: li.pickup_date ? String(li.pickup_date).slice(0, 10) : '',
        delivery: li.delivery_date ? String(li.delivery_date).slice(0, 10) : ''
      };
    });
    setLineItemAllocations(seedAlloc);
    setLineItemDates(seedDates);
    // A job that is already line-allocated should reopen on the per-line tab, not the
    // whole-job tab, so you never accidentally stack a whole-job allocation on top.
    setAllocateMode(existing.length > 0 ? 'line_items' : 'whole');
    setShowAllocateModal(true);
  };

  const handleViewJob = (job) => {
    setViewingJob(job);
    setShowViewModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setJobForm({
      client_id: job.client_id || '',
      commodity: job.commodity || '',
      commodity_type: job.commodity_type || 'mining',
      total_quantity_mt: job.total_quantity_mt || '',
      rate_per_mt: job.rate_per_mt || '',
      subbie_target_price: job.subbie_target_price || '',
      load_size_per_truck: job.load_size_per_truck || '',
      origin: job.origin || '',
      destination: job.destination || '',
      distance_km: job.distance_km || '',
      start_date: job.start_date || '',
      estimated_end_date: job.estimated_end_date || '',
      priority: job.priority || 'normal',
      notes: job.notes || '',
      rfq_client_name: '',
      rfq_contact_person: '',
      rfq_contact_email: '',
      rfq_contact_phone: '',
      rfq_company_address: '',
      // ✅ NEW: Quote Type Specific Fields
      quote_type: job.quote_type || 'minerals',
      wet_or_dry: job.wet_or_dry || 'dry',
      truck_type: job.truck_type || 'side_tipper',
      truck_type_other: job.truck_type_other || '',
      job_description: job.job_description || '',
      services: job.services || '',
      hourly_rate: job.hourly_rate || '',
      estimated_hours: job.estimated_hours || '',
      items: job.items || [],
      // ✅ PHASE 5: Container fields
      container_count: job.container_count || 1,
      container_size: job.container_size || '6m',
      container_type: job.container_type || 'dry',
      is_empty: job.is_empty !== undefined ? job.is_empty : true,
      cross_border: job.cross_border === true,
      site_loading: job.site_loading === true,
      repo_move: job.repo_move === true,
      combined_weight_t: job.combined_weight_t || ''
    });
    setJobType(job.quote_type || 'minerals');
    setIsRFQ(false);
    setShowEditModal(true);
  };

  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;
    try {
      const response = await fetch(`${API_URL}/jobs/${jobToDelete.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Job deleted successfully!');
        setShowDeleteConfirm(false);
        setJobToDelete(null);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      toast.error('Failed to delete job');
    }
  };

  // ✅ View Allocations handler
  const handleViewAllocations = (job) => {
    setSelectedJobForAllocationDetail(job);
    setShowAllocationDetailModal(true);
  };

  // ✅ Update allocation handler (keepModalOpen = inline edit inside the Allocations modal)
  const handleUpdateAllocation = async (allocationId, newTruckCount, keepModalOpen = false) => {
    try {
      const response = await fetch(`${API_URL}/job-requirements/${allocationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trucks_required: newTruckCount })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Truck count updated!');
        fetchData();
        if (!keepModalOpen) setShowAllocationDetailModal(false);
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (err) {
      console.error('Error updating allocation:', err);
      toast.error('Failed to update');
    }
  };

  // ✅ Inline truck-count editing inside the Allocations modal (no native prompt)
  const [editingAllocId, setEditingAllocId] = useState(null);
  const [allocTruckDraft, setAllocTruckDraft] = useState('');

  const startEditAllocTrucks = (alloc) => {
    setEditingAllocId(alloc.id);
    setAllocTruckDraft(String(alloc.trucks_required ?? ''));
  };

  const saveEditAllocTrucks = (alloc) => {
    const n = parseInt(allocTruckDraft, 10);
    if (!n || n <= 0) {
      toast.error('Enter a truck count greater than zero');
      return;
    }
    setEditingAllocId(null);
    if (n === alloc.trucks_required) return;
    handleUpdateAllocation(alloc.id, n, true);
  };

  // ✅ Unassign a LINE allocation (clears the subbie + cancels its pending payable)
  const unassignLine = async (line) => {
    try {
      const res = await fetch(`${API_URL}/job-line-items/${line.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subcontractor_id: null, assignment_status: 'unassigned' })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('✅ Line unassigned — its pending payable was cancelled');
        setShowAllocationDetailModal(false);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to unassign line');
      }
    } catch (err) {
      console.error('Error unassigning line:', err);
      toast.error('Failed to unassign line');
    }
  };

  // ✅ Edit the quantity (trucks) on a LINE allocation
  const saveLineQuantity = async (line, qty) => {
    try {
      const res = await fetch(`${API_URL}/job-line-items/${line.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: qty })
      });
      const data = await res.json();
      if (data.success) { toast.success('✅ Quantity updated'); fetchData(); }
      else toast.error(data.error || 'Failed to update quantity');
    } catch (err) {
      console.error('Error updating quantity:', err);
      toast.error('Failed to update quantity');
    }
  };

  // ✅ Edit the collection / delivery dates on an ALREADY-ALLOCATED line.
  // The dates live on job_line_items (pickup_date / delivery_date); the PUT handler passes the
  // body straight through and only re-syncs the subbie payable when the SUBCONTRACTOR changes,
  // so a date-only save is safe and never touches the OPL payable.
  const saveLineDates = async (line, pickup, delivery) => {
    const patch = {};
    if (pickup) patch.pickup_date = pickup;
    if (delivery) patch.delivery_date = delivery;
    if (Object.keys(patch).length === 0) return;
    try {
      const res = await fetch(`${API_URL}/job-line-items/${line.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to update dates');
      // Update local state so the modal stays open and the job card agrees immediately.
      setLineItemsByJob(prev => {
        const copy = { ...prev };
        Object.keys(copy).forEach(jobId => {
          copy[jobId] = (copy[jobId] || []).map(x => (x.id === line.id ? { ...x, ...patch } : x));
        });
        return copy;
      });
      toast.success('📅 Dates updated');
    } catch (err) {
      console.error('Error updating line dates:', err);
      toast.error('Failed to update dates: ' + err.message);
    }
  };

  const [removeAllocTarget, setRemoveAllocTarget] = useState(null); // allocation id to remove

  // ✅ Remove allocation handler
  const handleRemoveAllocation = (allocationId) => {
    setRemoveAllocTarget(allocationId);
  };

  const performRemoveAllocation = async () => {
    if (!removeAllocTarget) return;
    try {
      const response = await fetch(`${API_URL}/job-requirements/${removeAllocTarget}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Allocation removed!');
        fetchData();
        setShowAllocationDetailModal(false);
      } else {
        toast.error(data.error || 'Failed to remove');
      }
    } catch (err) {
      console.error('Error removing allocation:', err);
      toast.error('Failed to remove');
    }
    setRemoveAllocTarget(null);
  };

  const handleOriginChange = (address, coords) => {
    setOriginCoords(coords);
    setJobForm(prev => ({ ...prev, origin: address }));
  };

  const handleDestinationChange = (address, coords) => {
    setDestCoords(coords);
    setJobForm(prev => ({ ...prev, destination: address }));
  };

  // 🌍 AUTO CROSS-BORDER: if origin + destination countries differ, flag it.
  const applyCrossBorderFromCountries = (oc, dc) => {
    const o = (oc || '').toLowerCase().trim();
    const d = (dc || '').toLowerCase().trim();
    if (o && d && o !== d) {
      setJobForm(prev => ({ ...prev, cross_border: true }));
      return true;
    }
    return false;
  };

  const handleDistanceCalculated = (data) => {
    setJobForm(prev => ({ ...prev, distance_km: data.distance_km }));
  };

  // ✅ Calculate profit margin
  const calculateMargin = () => {
    const rate = parseFloat(jobForm.rate_per_mt) || 0;
    const subbiePrice = parseFloat(jobForm.subbie_target_price) || 0;
    if (rate > 0 && subbiePrice > 0) {
      const profit = rate - subbiePrice;
      const percentage = (profit / rate) * 100;
      return { profit, percentage };
    }
    return null;
  };

  // ✅ Calculate CPK (Cost Per Kilometer)
  const calculateCPK = () => {
    const price = parseFloat(jobForm.subbie_target_price) || 0;
    const loadSize = parseFloat(jobForm.load_size_per_truck) || 0;
    const distance = parseFloat(jobForm.distance_km) || 0;
    if (price > 0 && loadSize > 0 && distance > 0) {
      return (price * loadSize) / distance;
    }
    return null;
  };

  // ✅ Render job type specific fields in create modal
  const renderJobTypeFields = () => {
    const type = jobForm.quote_type || 'minerals';
    
    switch(type) {
      case 'minerals':
      case 'agri':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Wet / Dry</label>
              <select
                value={jobForm.wet_or_dry}
                onChange={(e) => setJobForm({ ...jobForm, wet_or_dry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="dry">Dry</option>
                <option value="wet">Wet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type</label>
              <select
                value={jobForm.truck_type}
                onChange={(e) => {
                  const val = e.target.value;
                  setJobForm({ ...jobForm, truck_type: val });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="side_tipper">Side Tipper</option>
                <option value="flat_deck">Flat Deck</option>
                <option value="lowbed">Lowbed</option>
                <option value="tautliner">Tautliner</option>
                <option value="grain_side_tipper">Grain Side Tipper</option>
                <option value="extended_trailer">Extended Trailer</option>
                <option value="step_deck">Step Deck</option>
                <option value="other">Other</option>
              </select>
            </div>
            {jobForm.truck_type === 'other' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type Description</label>
                <input
                  type="text"
                  value={jobForm.truck_type_other}
                  onChange={(e) => setJobForm({ ...jobForm, truck_type_other: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  placeholder="Describe truck type..."
                />
              </div>
            )}
          </div>
        );
      
      case 'container':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Container Count</label>
              <input
                type="number"
                min="1"
                max="3"
                value={jobForm.container_count}
                onChange={(e) => setJobForm({ ...jobForm, container_count: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Container Size</label>
              <select
                value={jobForm.container_size}
                onChange={(e) => setJobForm({ ...jobForm, container_size: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
              >
                <option value="6m">6m (20ft)</option>
                <option value="12m">12m (40ft)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Container Type</label>
              <select
                value={jobForm.container_type}
                onChange={(e) => setJobForm({ ...jobForm, container_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
              >
                <option value="dry">Dry</option>
                <option value="fridge">Fridge / Reefer</option>
                <option value="open_top">Open Top</option>
                <option value="flat_rack">Flat Rack</option>
                <option value="tank">Tank</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empty / Loaded</label>
              <select
                value={jobForm.is_empty ? 'empty' : 'loaded'}
                onChange={(e) => setJobForm({ ...jobForm, is_empty: e.target.value === 'empty' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
              >
                <option value="empty">Empty</option>
                <option value="loaded">Loaded</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Combined Weight (tonnes)</label>
              <input
                type="number"
                step="0.5"
                value={jobForm.combined_weight_t}
                onChange={(e) => setJobForm({ ...jobForm, combined_weight_t: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
                placeholder="e.g. 26"
              />
            </div>
            {parseInt(jobForm.combined_weight_t) > 27 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <FaExclamationTriangle /> Over 27t — side-lifter not legal, will use crane/super-link.
              </div>
            )}
            <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={jobForm.cross_border}
                  onChange={(e) => setJobForm({ ...jobForm, cross_border: e.target.checked })}
                  className="rounded"
                />
                Cross-Border move
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={jobForm.site_loading === true}
                  onChange={(e) => setJobForm({ ...jobForm, site_loading: e.target.checked })}
                  className="rounded"
                />
                Client loads / offloads
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={jobForm.repo_move === true}
                  onChange={(e) => setJobForm({ ...jobForm, repo_move: e.target.checked })}
                  className="rounded"
                />
                Repo / move extra on site
              </label>
            </div>
          </div>
        );

      case 'abnormal':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Description</label>
            <textarea
              value={jobForm.job_description}
              onChange={(e) => setJobForm({ ...jobForm, job_description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              rows="3"
              placeholder="Describe the abnormal load requirements..."
            />
          </div>
        );
      
      case 'general':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Description</label>
              <input
                type="text"
                value={jobForm.services}
                onChange={(e) => setJobForm({ ...jobForm, services: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. General Transport"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
              <input
                type="number"
                value={jobForm.estimated_hours}
                onChange={(e) => setJobForm({ ...jobForm, estimated_hours: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (R)</label>
              <input
                type="number"
                value={jobForm.hourly_rate}
                onChange={(e) => setJobForm({ ...jobForm, hourly_rate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. 800"
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setCreatingJob(true);
    try {
      let clientId = jobForm.client_id;

      if (isRFQ) {
        if (!jobForm.rfq_client_name || !jobForm.rfq_contact_email) {
          toast.error('Please fill in at least Client Name and Email for the RFQ');
          setCreatingJob(false);
          return;
        }

        const clientPayload = {
          company_name: jobForm.rfq_client_name,
          contact_person: jobForm.rfq_contact_person || jobForm.rfq_client_name,
          email: jobForm.rfq_contact_email,
          phone: jobForm.rfq_contact_phone || '',
          address: jobForm.rfq_company_address || '',
          user_id: user.id
        };

        const clientResponse = await fetch(`${API_URL}/clients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clientPayload)
        });
        const clientData = await clientResponse.json();
        if (clientData.success) {
          clientId = clientData.client.id;
          toast.success(`✅ New client "${jobForm.rfq_client_name}" created!`);
          const clientsData = await clients.getAll();
          setClientsList(clientsData.clients || []);
        } else {
          toast.error(clientData.error || 'Failed to create client');
          setCreatingJob(false);
          return;
        }
      }

      const payload = {
        ...jobForm,
        client_id: clientId,
        total_quantity_mt: parseFloat(jobForm.total_quantity_mt) || 0,
        rate_per_mt: parseFloat(jobForm.rate_per_mt) || 0,
        subbie_target_price: parseFloat(jobForm.subbie_target_price),
        load_size_per_truck: parseFloat(jobForm.load_size_per_truck) || null,
        distance_km: parseInt(jobForm.distance_km) || null,
        created_by: user.id || 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
        status: 'pending',
        is_rfq: isRFQ,
        // ✅ NEW: Quote Type Specific Fields
        quote_type: jobForm.quote_type || 'minerals',
        wet_or_dry: jobForm.wet_or_dry || 'dry',
        truck_type: jobForm.truck_type || 'side_tipper',
        truck_type_other: jobForm.truck_type === 'other' ? jobForm.truck_type_other : null,
        job_description: jobForm.job_description || '',
        services: jobForm.services || '',
        hourly_rate: parseFloat(jobForm.hourly_rate) || 0,
        estimated_hours: parseFloat(jobForm.estimated_hours) || 0,
        items: jobForm.items || [],
        // ✅ PHASE 5: Container fields
        container_count: jobForm.container_count ? parseInt(jobForm.container_count) : null,
        container_size: jobForm.container_size || null,
        container_type: jobForm.container_type || null,
        is_empty: jobForm.is_empty !== undefined ? jobForm.is_empty : null,
        cross_border: jobForm.cross_border === true || (jobForm.origin_country && jobForm.destination_country && jobForm.origin_country.toLowerCase().trim() !== jobForm.destination_country.toLowerCase().trim()),
        origin_country: jobForm.origin_country || null,
        destination_country: jobForm.destination_country || null,
        site_loading: jobForm.site_loading === true,
        repo_move: jobForm.repo_move === true,
        combined_weight_t: jobForm.combined_weight_t ? parseFloat(jobForm.combined_weight_t) : null
      };

      const response = await fetch(`${API_URL}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Job created successfully${isRFQ ? ' (RFQ)' : ''}!`);
        setShowCreateModal(false);
        setJobForm({
          client_id: '',
          commodity: '',
          commodity_type: 'mining',
          total_quantity_mt: '',
          rate_per_mt: '',
          subbie_target_price: '',
          load_size_per_truck: '',
          origin: '',
          destination: '',
          distance_km: '',
          start_date: '',
          estimated_end_date: '',
          priority: 'normal',
          notes: '',
          rfq_client_name: '',
          rfq_contact_person: '',
          rfq_contact_email: '',
          rfq_contact_phone: '',
          rfq_company_address: '',
          quote_type: 'minerals',
          wet_or_dry: 'dry',
          truck_type: 'side_tipper',
          truck_type_other: '',
          job_description: '',
          services: '',
          hourly_rate: '',
          estimated_hours: '',
          items: []
        });
        setIsRFQ(false);
        setJobType('minerals');
        setOriginCoords(null);
        setDestCoords(null);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to create job');
      }
    } catch (err) {
      console.error('Error creating job:', err);
      toast.error('Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    setCreatingJob(true);
    try {
      const payload = {
        ...jobForm,
        total_quantity_mt: parseFloat(jobForm.total_quantity_mt) || 0,
        rate_per_mt: parseFloat(jobForm.rate_per_mt) || 0,
        subbie_target_price: parseFloat(jobForm.subbie_target_price),
        load_size_per_truck: parseFloat(jobForm.load_size_per_truck) || null,
        distance_km: parseInt(jobForm.distance_km) || null,
        status: 'pending',
        // ✅ NEW: Quote Type Specific Fields
        quote_type: jobForm.quote_type || 'minerals',
        wet_or_dry: jobForm.wet_or_dry || 'dry',
        truck_type: jobForm.truck_type || 'side_tipper',
        truck_type_other: jobForm.truck_type === 'other' ? jobForm.truck_type_other : null,
        job_description: jobForm.job_description || '',
        services: jobForm.services || '',
        hourly_rate: parseFloat(jobForm.hourly_rate) || 0,
        estimated_hours: parseFloat(jobForm.estimated_hours) || 0,
        items: jobForm.items || [],
        // ✅ PHASE 5: Container fields
        container_count: jobForm.container_count ? parseInt(jobForm.container_count) : null,
        container_size: jobForm.container_size || null,
        container_type: jobForm.container_type || null,
        is_empty: jobForm.is_empty !== undefined ? jobForm.is_empty : null,
        cross_border: jobForm.cross_border === true || (jobForm.origin_country && jobForm.destination_country && jobForm.origin_country.toLowerCase().trim() !== jobForm.destination_country.toLowerCase().trim()),
        origin_country: jobForm.origin_country || null,
        destination_country: jobForm.destination_country || null,
        site_loading: jobForm.site_loading === true,
        repo_move: jobForm.repo_move === true,
        combined_weight_t: jobForm.combined_weight_t ? parseFloat(jobForm.combined_weight_t) : null
      };

      const response = await fetch(`${API_URL}/jobs/${selectedJob.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Job updated successfully!');
        setShowEditModal(false);
        setSelectedJob(null);
        setJobForm({
          client_id: '',
          commodity: '',
          commodity_type: 'mining',
          total_quantity_mt: '',
          rate_per_mt: '',
          subbie_target_price: '',
          load_size_per_truck: '',
          origin: '',
          destination: '',
          distance_km: '',
          start_date: '',
          estimated_end_date: '',
          priority: 'normal',
          notes: '',
          rfq_client_name: '',
          rfq_contact_person: '',
          rfq_contact_email: '',
          rfq_contact_phone: '',
          rfq_company_address: '',
          quote_type: 'minerals',
          wet_or_dry: 'dry',
          truck_type: 'side_tipper',
          truck_type_other: '',
          job_description: '',
          services: '',
          hourly_rate: '',
          estimated_hours: '',
          items: []
        });
        setIsRFQ(false);
        setJobType('minerals');
        setOriginCoords(null);
        setDestCoords(null);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to update job');
      }
    } catch (err) {
      console.error('Error updating job:', err);
      toast.error('Failed to update job');
    } finally {
      setCreatingJob(false);
    }
  };

  // ✅ handleAllocateSubbies - Supports adding more trucks to existing allocations
  const [selectedSubbies, setSelectedSubbies] = useState({});
  const [truckCounts, setTruckCounts] = useState({});
  const [lineItemDates, setLineItemDates] = useState({}); // per-line { pickup, delivery }

  // ✅ handleAllocateLineItems - assign each job line item to a subbie (per-line allocation).
  // FIX: operates on the REAL job_line_items rows (matched by line_number). Previously it
  // PUT to `/job-line-items/{quote-item-id}` which 404'd silently (quote ids aren't
  // job_line_items ids), so allocation never created a row.
  const handleAllocateLineItems = async () => {
    setAllocating(true);
    try {
      const items = selectedJob.items || [];
      const entries = Object.entries(lineItemAllocations).filter(([, sid]) => sid);
      if (items.length === 0) {
        toast.error('This job has no line items to allocate');
        return;
      }
      if (entries.length === 0) {
        toast.error('Please assign at least one line item to a subcontractor');
        return;
      }

      // Existing execution rows for this job, keyed by line number.
      const rowsResp = await fetch(`${API_URL}/job-line-items?job_id=${selectedJob.id}`);
      const rowsData = await rowsResp.json();
      const rows = (rowsData.success && rowsData.line_items) || [];
      const rowsByLine = {};
      rows.forEach((r) => { rowsByLine[parseInt(r.line_number) || 0] = r; });

      let updated = 0;
      for (const [idx, subbieId] of entries) {
        const item = items[idx];
        if (!item) continue;
        const lineNumber = (parseInt(idx) || 0) + 1;
        const existingRow = rowsByLine[lineNumber];
        const clientRate = parseFloat(item.unit_price) || 0;
        const costPer = parseFloat(item.cost_price) || 0;
        const qty = parseFloat(item.quantity) || 1;
        const marginPct = item.margin_percentage !== undefined && item.margin_percentage !== null
          ? item.margin_percentage
          : (clientRate ? Math.round(((clientRate - costPer) / clientRate) * 1000) / 10 : 0);

        const lineDates = lineItemDates[idx] || {};
        const datePayload = {};
        if (lineDates.pickup) datePayload.pickup_date = lineDates.pickup;
        if (lineDates.delivery) datePayload.delivery_date = lineDates.delivery;

        const common = {
          subcontractor_id: subbieId,
          assignment_status: 'assigned',
          rate: clientRate,
          total: clientRate * qty,
          cost_price: costPer,
          client_price: clientRate,
          margin_percentage: marginPct,
          margin_amount: Math.round(((clientRate - costPer) * qty) * 100) / 100
        };

        let resp;
        if (existingRow) {
          resp = await fetch(`${API_URL}/job-line-items/${existingRow.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...common, ...datePayload })
          });
        } else {
          resp = await fetch(`${API_URL}/job-line-items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              job_id: selectedJob.id,
              line_number: lineNumber,
              title: item.description || `Line ${lineNumber}`,
              description: item.description || '',
              quantity: qty,
              unit: 'load',
              ...common,
              ...datePayload
            })
          });
        }
        const data = await resp.json();
        if (data.success) updated++;
        else console.warn('Line-item allocation failed:', data.error);
      }
      toast.success(`✅ ${updated} line item(s) allocated`);
      setShowAllocateModal(false);
      setLineItemAllocations({});
      setLineItemDates({});
      fetchData();
    } catch (error) {
      console.error('Error allocating line items:', error);
      toast.error('Failed to allocate line items: ' + error.message);
    } finally {
      setAllocating(false);
    }
  };

  // ✅ Record completed work — admin back-fills a line that was done OFF-SYSTEM.
  const openRecordWork = (job) => {
    const lines = (lineItemsByJob[job.id] || []).filter(li => li.subcontractor_id);
    const drafts = {};
    const today = new Date().toISOString().split('T')[0];
    lines.forEach(li => {
      drafts[li.id] = {
        started_at: li.pickup_date || today,
        ended_at: li.delivery_date || today,
        cost_override: li.cost_price != null ? String(li.cost_price) : '',
        notes: ''
      };
    });
    setRecordDrafts(drafts);
    setRecordJob(job);
    setShowRecordModal(true);
  };

  const handleRecordDraft = (lineId, field, value) => {
    setRecordDrafts(prev => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), [field]: value } }));
  };

  const submitRecordCompleted = async (line) => {
    const d = recordDrafts[line.id] || {};
    setRecordingId(line.id);
    try {
      const response = await fetch(`${API_URL}/job-line-items/${line.id}/record-completed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subcontractor_id: line.subcontractor_id,
          started_at: d.started_at ? `${d.started_at}T08:00:00` : undefined,
          ended_at: d.ended_at ? `${d.ended_at}T17:00:00` : undefined,
          cost_override: d.cost_override && d.cost_override !== '' ? parseFloat(d.cost_override) : undefined,
          notes: d.notes || undefined
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Completed work recorded');
        fetchData();
      } else {
        toast.error(data.error || 'Failed to record completed work');
      }
    } catch (error) {
      console.error('Error recording completed work:', error);
      toast.error('Failed to record completed work: ' + (error.message || error));
    } finally {
      setRecordingId(null);
    }
  };

  const handleAllocateSubbies = async () => {
    setAllocating(true);
    try {
      const selected = Object.keys(selectedSubbies).filter(id => selectedSubbies[id]);
      if (selected.length === 0) {
        toast.error('Please select at least one subcontractor');
        return;
      }

      const currentAllocations = allocations[selectedJob.id] || [];
      
      for (const subbieId of selected) {
        const trucksRequired = truckCounts[subbieId] || 1;
        
        const existingAllocation = currentAllocations.find(
          alloc => alloc.subcontractor_id === subbieId
        );

        let response;
        if (existingAllocation) {
          const newTruckCount = (existingAllocation.trucks_required || 0) + trucksRequired;
          response = await fetch(`${API_URL}/job-requirements/${existingAllocation.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ trucks_required: newTruckCount })
          });
        } else {
          const payload = {
            job_id: selectedJob.id,
            subcontractor_id: subbieId,
            trucks_required: trucksRequired
          };
          response = await fetch(`${API_URL}/job-requirements`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to allocate');
        }
      }

      if (selectedJob.status !== 'new') {
        await fetch(`${API_URL}/jobs/${selectedJob.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'new' })
        });
      }

      toast.success('✅ Subcontractors allocated/updated successfully!');
      setShowAllocateModal(false);
      setSelectedSubbies({});
      setTruckCounts({});
      fetchData();
    } catch (error) {
      console.error('Error allocating subbies:', error);
      toast.error('Failed to allocate subcontractors: ' + error.message);
    } finally {
      setAllocating(false);
    }
  };

  const handleSubbieToggle = (subbieId) => {
    setSelectedSubbies(prev => ({
      ...prev,
      [subbieId]: !prev[subbieId]
    }));
    if (!selectedSubbies[subbieId]) {
      setTruckCounts(prev => ({
        ...prev,
        [subbieId]: 1
      }));
    }
  };

  const handleTruckCountChange = (subbieId, count) => {
    setTruckCounts(prev => ({
      ...prev,
      [subbieId]: Math.max(1, parseInt(count) || 1)
    }));
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-gray-100 text-gray-600',
      'normal': 'bg-blue-100 text-blue-600',
      'high': 'bg-orange-100 text-orange-600',
      'urgent': 'bg-red-100 text-red-600'
    };
    return colors[priority?.toLowerCase()] || colors.normal;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'low': 'LOW',
      'normal': 'NORMAL',
      'high': 'HIGH',
      'urgent': 'URGENT'
    };
    return labels[priority?.toLowerCase()] || 'NORMAL';
  };

  const getQuoteTypeLabel = (type) => {
    const labels = {
      'minerals': '⛏️ Minerals',
      'agri': '🌾 Agri',
      'abnormal': '🚛 Abnormal',
      'container': '📦 Containers',
      'general': '📋 General'
    };
    return labels[type] || type;
  };

  const jobTypeColors = {
    'minerals': 'border-red-400',
    'agri': 'border-green-400',
    'abnormal': 'border-purple-400',
    'container': 'border-yellow-400',
    'general': 'border-blue-400'
  };

  const jobTypeBadgeColors = {
    'minerals': 'bg-red-100 text-red-700',
    'agri': 'bg-green-100 text-green-700',
    'abnormal': 'bg-purple-100 text-purple-700',
    'container': 'bg-yellow-100 text-yellow-700',
    'general': 'bg-blue-100 text-blue-700'
  };

  const getQuoteRefFromJob = (job) => {
    // Notes contain "Created from quote OPL-00001-ABN-FOR"
    const match = job.notes?.match(/OPL-\d{5}-[A-Z]{3}-[A-Z]{3}/);
    return match ? match[0] : null;
  };

  const selectedCount = Object.values(selectedSubbies).filter(Boolean).length;

  // Lines on the currently-selected job that already have a subbie. Drives the "Allocated"
  // badge + pre-fill in the Allocate modal so a line can never be allocated twice by accident.
  const allocatedLineNumbers = new Set(
    (selectedJob ? (lineItemsByJob[selectedJob.id] || []) : [])
      .filter(li => li.subcontractor_id)
      .map(li => parseInt(li.line_number, 10) || 0)
  );

  if (loading) {
    return (
      <AdminLayout activeTab="newjobs" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading new jobs...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab="newjobs" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Jobs</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="newjobs" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📋 New Jobs</h1>
          <p className="text-gray-500 text-sm">Jobs waiting for allocation</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {(user.role === 'super_admin' || (user.permissions || []).includes('edit_jobs')) && (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors"
            >
              <FaPlus size={14} /> Create Job
            </button>
          )}
          <button className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium hover:border-green hover:text-green transition-colors">
            <FaSearch size={14} /> Search
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaBox size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{jobsList.length}</div>
              <div className="text-xs text-gray-500">New Jobs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
              <FaExclamationTriangle size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{jobsList.filter(j => j.priority === 'urgent').length}</div>
              <div className="text-xs text-gray-500">Urgent Jobs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaUsers size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">{subbiesList.length}</div>
              <div className="text-xs text-gray-500">Available Subbies</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
              <FaTruck size={18} />
            </div>
            <div>
              <div className="text-2xl font-bold text-navy">
                {Object.values(allocations).reduce((sum, arr) => sum + arr.length, 0)}
              </div>
              <div className="text-xs text-gray-500">Allocations</div>
            </div>
          </div>
        </div>
      </div>

      {jobsList.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold text-navy mb-2">No New Jobs</h3>
          <p className="text-gray-500">All jobs have been allocated. Create a new job to get started.</p>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            <FaPlus className="inline mr-2" /> Create New Job
          </button>
        </div>
      ) : (
        jobsList.map((job) => {
          const jobAllocations = allocations[job.id] || [];
          const jobLineItems = (lineItemsByJob[job.id] || []).filter(li => li.subcontractor_id);
          const totalTrucks = jobAllocations.reduce((sum, a) => sum + (a.trucks_required || 0), 0);
          const hasWholeAllocations = jobAllocations.length > 0;
          const hasLineAllocations = jobLineItems.length > 0;
          const isAllocated = hasWholeAllocations || hasLineAllocations;
          const jobType = job.quote_type || 'minerals';
          
          return (
            <div key={job.id} className={`bg-white rounded-xl border border-gray-200 border-l-4 ${jobTypeColors[jobType] || 'border-l-gray-200'} p-4 mb-3 shadow-sm hover:shadow-md transition-shadow`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-navy">{job.job_number}</h3>
                    {job.is_rfq && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-600">
                        <FaFileAlt className="inline mr-1" size={10} /> RFQ
                      </span>
                    )}
                    {/* ✅ NEW: Quote Type Badge */}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${jobTypeBadgeColors[jobType] || 'bg-blue-100 text-blue-600'}`}>
                      {getQuoteTypeLabel(jobType)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(job.priority)}`}>
                      {getPriorityLabel(job.priority)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isAllocated ? 'bg-blue-100 text-blue-600' : 'bg-yellow-100 text-yellow-600'}`}>
                      {isAllocated ? 'ALLOCATED' : 'PENDING'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <FaBuilding className="text-green" />
                    <span className="font-medium">{job.client?.company_name || 'Unknown Client'}</span>
                    {job.price_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        job.price_type === 'quoted' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
                      }`}>
                        {job.price_type === 'quoted' ? '💰 Quoted Price' : '🎯 Target Price'}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                    {/* Minerals / Agri: commodity, tonnage, rate, route */}
                    {(jobType === 'minerals' || jobType === 'agri') && (
                      <>
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} />
                          {job.commodity || 'Various'} — {job.total_quantity_mt || 0} MT
                        </span>
                        {job.rate_per_mt > 0 && (
                          <span className="flex items-center gap-1 font-medium text-navy">
                            R{job.rate_per_mt}/MT
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} />
                          {job.origin} → {job.destination}
                        </span>
                        {job.wet_or_dry && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{job.wet_or_dry}</span>
                        )}
                        {job.truck_type && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{job.truck_type}</span>
                        )}
                      </>
                    )}
                    {/* Abnormal: description, items count, route */}
                    {jobType === 'abnormal' && (
                      <>
                        <span className="flex items-center gap-1">
                          <FaInfoCircle size={14} />
                          {job.job_description || 'No description'}
                        </span>
                        {job.items && job.items.length > 0 && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                            {job.items.length} item{job.items.length > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} />
                          {job.origin || 'N/A'} → {job.destination || 'N/A'}
                        </span>
                      </>
                    )}
                    {/* 📦 Containers: count × size type, truck config, route */}
                    {jobType === 'container' && (
                      <>
                        <span className="flex items-center gap-1 font-medium text-navy">
                          <FaBox size={14} className="text-yellow-500" />
                          {job.container_count || 1} × {job.container_size || '6m'} {job.container_type || 'dry'} container{(job.container_count || 1) > 1 ? 's' : ''}
                        </span>
                        {job.truck_type && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                            {String(job.truck_type).replace(/_/g, ' ')}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FaMapMarkerAlt size={14} />
                          {job.origin || 'N/A'} → {job.destination || 'N/A'}
                        </span>
                        {job.cross_border === true && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            🛂 Cross-Border
                          </span>
                        )}
                      </>
                    )}
                    {/* General: services, hourly rate, hours */}
                    {jobType === 'general' && (
                      <>
                        <span className="flex items-center gap-1">
                          <FaInfoCircle size={14} />
                          {job.services || 'General Services'}
                        </span>
                        {job.hourly_rate > 0 && (
                          <span className="font-medium text-navy">R{job.hourly_rate}/hr</span>
                        )}
                        {job.estimated_hours > 0 && (
                          <span>{job.estimated_hours} hrs</span>
                        )}
                      </>
                    )}
                    <span className="flex items-center gap-1 ml-auto">
                      <FaCalendarAlt size={14} />
                      {job.start_date || 'No date'}
                    </span>
                  </div>

                  {isAllocated && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-navy">Allocated Subcontractors</span>
                        <span className="text-sm text-gray-500 font-bold">
                          {hasWholeAllocations
                            ? `🚛 ${totalTrucks} trucks total`
                            : `📋 ${jobLineItems.length} line item(s) assigned`}
                        </span>
                      </div>
                      {hasWholeAllocations && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {jobAllocations.map((alloc) => {
                            const subbie = subbiesList.find(s => s.id === alloc.subcontractor_id);
                            return (
                              <div key={alloc.id} className="flex items-center gap-1 bg-white px-3 py-1 rounded-full border border-gray-200 text-xs">
                                <FaUserTie size={12} className="text-gray-400" />
                                <span>{subbie?.company_name?.split(' ')[0] || 'Unknown'}</span>
                                <span className="text-gray-400">{alloc.trucks_required || 0} trucks</span>
                                <span className="text-blue-500 ml-1">Pending</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {hasLineAllocations && (
                        <div className="flex flex-col gap-1">
                          {jobLineItems.map(li => {
                            const s = subbiesList.find(x => x.id === li.subcontractor_id);
                            return (
                              <div key={li.id} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-xs">
                                <FaUserTie size={12} className="text-purple-500" />
                                <span className="font-medium text-navy">{s?.company_name?.split(' ')[0] || 'Unknown'}</span>
                                <span className="text-gray-500 truncate">{li.title || li.description || 'Line item'}</span>
                                <span className="ml-auto flex items-center gap-2 text-gray-400">
                                  <span className="text-purple-600 font-semibold">L{li.line_number}</span>
                                  {li.pickup_date && <span>📅 {String(li.pickup_date).slice(5)}</span>}
                                  {li.delivery_date && <span>→ {String(li.delivery_date).slice(5)}</span>}
                                  {li.subcontractor_id && (
                                    <PurchaseOrderButton
                                      jobId={job.id}
                                      subcontractorId={li.subcontractor_id}
                                      subbieName={s?.company_name || 'Subcontractor'}
                                      iconOnly
                                    />
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {isAllocated ? (
                      <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium border border-green-200">
                        <FaCheck size={14} /> Allocated ✅
                      </span>
                    ) : (
                      <button 
                        onClick={() => handleAllocate(job)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        <FaUserTie size={14} /> Allocate
                      </button>
                    )}
                    
                    {isAllocated && (
                      <button 
                        onClick={() => handleViewAllocations(job)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-green hover:text-green transition-colors"
                      >
                        <FaList size={14} /> View Allocations {hasWholeAllocations ? `(${totalTrucks} trucks)` : hasLineAllocations ? `(${jobLineItems.length} lines)` : ''}
                      </button>
                    )}
                    {hasLineAllocations && (
                      <button
                        onClick={() => openRecordWork(job)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                      >
                        ✅ Record Completed Work
                      </button>
                    )}
                    
                    <button 
                      onClick={() => handleViewJob(job)}

                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-green hover:text-green transition-colors"
                    >
                      <FaEye size={14} /> View
                    </button>
                    <button 
                      onClick={() => handleEditJob(job)}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-yellow-500 hover:text-yellow-500 transition-colors"
                    >
                      <FaEdit size={14} /> Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteJob(job)}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:border-red-500 hover:text-red-500 transition-colors"
                    >
                      <FaTrash size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* ✅ ALLOCATION DETAIL MODAL */}
      {showAllocationDetailModal && selectedJobForAllocationDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAllocationDetailModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-navy">🚛 Allocations</h2>
                <p className="text-sm text-gray-500">{selectedJobForAllocationDetail.job_number}</p>
                <p className="text-xs text-blue-600 font-medium">
                  Total: {allocations[selectedJobForAllocationDetail.id]?.reduce((sum, a) => sum + (a.trucks_required || 0), 0)} || 0} truck(s) · {(lineItemsByJob[selectedJobForAllocationDetail.id] || []).filter(li => li.subcontractor_id).length} line item(s) allocated
                </p>
              </div>
              <button onClick={() => setShowAllocationDetailModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              {(allocations[selectedJobForAllocationDetail.id]?.length || 0) === 0 &&
               (lineItemsByJob[selectedJobForAllocationDetail.id] || []).filter(li => li.subcontractor_id).length === 0 ? (
                <p className="text-center text-gray-500 py-8">No allocations yet</p>
              ) : (
                <div className="space-y-3">
                  {allocations[selectedJobForAllocationDetail.id]?.map((alloc) => {
                    const subbie = subbiesList.find(s => s.id === alloc.subcontractor_id);
                    return (
                      <div key={alloc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div>
                          <p className="font-medium text-navy">{subbie?.company_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">
                            {alloc.trucks_required} truck{alloc.trucks_required > 1 ? 's' : ''} allocated
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {editingAllocId === alloc.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="1"
                                autoFocus
                                value={allocTruckDraft}
                                onChange={(e) => setAllocTruckDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEditAllocTrucks(alloc);
                                  if (e.key === 'Escape') setEditingAllocId(null);
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-green"
                              />
                              <button
                                onClick={() => saveEditAllocTrucks(alloc)}
                                className="text-xs bg-green text-white px-2 py-1 rounded-lg hover:bg-navy transition-colors flex items-center gap-1"
                                title="Save truck count"
                              >
                                <FaCheck size={10} /> Save
                              </button>
                              <button
                                onClick={() => setEditingAllocId(null)}
                                className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
                                title="Cancel"
                              >
                                <FaTimes size={10} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1"
                                onClick={() => startEditAllocTrucks(alloc)}
                              >
                                <FaEdit size={10} /> Edit
                              </button>
                              <button 
                            className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
                            onClick={() => handleRemoveAllocation(alloc.id)}
                          >
                            <FaTrash size={10} /> Remove
                          </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            
              {/* ✅ LINE-ITEM ALLOCATIONS — per-line subbie, editable qty, unassign */}
              {(lineItemsByJob[selectedJobForAllocationDetail.id] || []).filter(li => li.subcontractor_id).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">By line item</p>
                  <div className="space-y-3">
                    {(lineItemsByJob[selectedJobForAllocationDetail.id] || []).filter(li => li.subcontractor_id).map((li) => {
                      const ls = subbiesList.find(s => s.id === li.subcontractor_id);
                      return (
                        <div key={li.id} className="p-3 border rounded-lg" style={{ borderColor: '#E8ECEF' }}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-navy truncate">
                                {li.line_number ? `Line ${li.line_number} · ` : ''}{li.title || li.description || 'Line item'}
                              </p>
                              <p className="text-sm text-gray-500">
                                {ls?.company_name || 'Unknown subbie'} · {li.quantity || 1} × {li.unit || 'load'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <input
                                type="number"
                                min="1"
                                defaultValue={li.quantity || 1}
                                title="Quantity / trucks on this line"
                                onBlur={(e) => { const q = parseInt(e.target.value, 10); if (q && q !== (li.quantity || 1)) saveLineQuantity(li, q); }}
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-green"
                              />
                              <button
                                onClick={() => unassignLine(li)}
                                className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1"
                              >
                                <FaTrash size={10} /> Unassign
                              </button>
                            </div>
                          </div>
                          {/* ✅ Collection + delivery dates stay EDITABLE after allocation. Previously
                              a changed collection date could not be corrected without unallocating. */}
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Collection date</label>
                              <input
                                type="date"
                                defaultValue={li.pickup_date ? String(li.pickup_date).slice(0, 10) : ''}
                                onChange={(e) => saveLineDates(li, e.target.value, null)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-green"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Delivery date</label>
                              <input
                                type="date"
                                defaultValue={li.delivery_date ? String(li.delivery_date).slice(0, 10) : ''}
                                onChange={(e) => saveLineDates(li, null, e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-green"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                Total: {allocations[selectedJobForAllocationDetail.id]?.reduce((sum, a) => sum + (a.trucks_required || 0), 0)} || 0} truck(s) · {(lineItemsByJob[selectedJobForAllocationDetail.id] || []).filter(li => li.subcontractor_id).length} line item(s)
              </span>
              <button 
                onClick={() => {
                  setShowAllocationDetailModal(false);
                  handleAllocate(selectedJobForAllocationDetail);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <FaPlus size={12} /> Allocate More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ALLOCATE MODAL - Pick subcontractor(s) + truck count */}
      {showAllocateModal && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAllocateModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-navy">🚛 Allocate Subcontractors</h2>
                <p className="text-sm text-gray-500">{selectedJob.job_number} · {selectedJob.client?.company_name || ''}</p>
              </div>
              <button onClick={() => setShowAllocateModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* ✅ Mode toggle: whole job vs per line item */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setAllocateMode('whole')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${allocateMode === 'whole' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:border-blue-400'}`}
                >
                  🎯 Allocate whole job
                </button>
                <button
                  type="button"
                  onClick={() => setAllocateMode('line_items')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${allocateMode === 'line_items' ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-600 hover:border-purple-400'}`}
                >
                  📋 Allocate by line item
                </button>
              </div>

              {allocateMode === 'whole' && (
              <>
              <p className="text-sm text-gray-600 mb-4">
                Select the subcontractor(s) and how many trucks each should provide for this job.
              </p>

              {subbiesList.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No subcontractors available. Add subcontractors first.</p>
              ) : (
                <div className="space-y-3">
                  {subbiesList.map((subbie) => {
                    const isChecked = !!selectedSubbies[subbie.id];
                    return (
                      <div key={subbie.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleSubbieToggle(subbie.id)}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-navy">{subbie.company_name}</p>
                          <p className="text-xs text-gray-500">
                            {subbie.contact_person || ''}{subbie.contact_person && subbie.phone ? ' · ' : ''}{subbie.phone || ''}
                          </p>
                        </div>
                        {isChecked && (
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500 font-medium">Trucks</label>
                            <input
                              type="number"
                              min="1"
                              value={truckCounts[subbie.id] || 1}
                              onChange={(e) => handleTruckCountChange(subbie.id, e.target.value)}
                              className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-green"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              </>
              )}

              {/* 📋 Allocate by line item */}
              {allocateMode === 'line_items' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Assign each line item to a subcontractor. Each subbie will only see their own line item.
                  </p>
                  {(selectedJob.items || []).filter(it => (parseFloat(it.unit_price) || 0) > 0).length === 0 ? (
                    <p className="text-center text-gray-500 py-8">This job has no allocatable line items yet. Add line items in Edit first.</p>
                  ) : (
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                      {(selectedJob.items || [])
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) => (parseFloat(item.unit_price) || 0) > 0)
                        .map(({ item, index }) => (
                        <div key={index} className={`border rounded-lg p-3 ${allocatedLineNumbers.has(index + 1) ? 'bg-green-50 border-green-200' : 'border-gray-200'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-navy text-sm">{item.description || 'Line item'}</p>
                              <p className="text-xs text-gray-500">
                                Qty {item.quantity || 1} · Client R{(parseFloat(item.unit_price) || 0).toFixed(2)} · Subbie R{(parseFloat(item.cost_price) || 0).toFixed(2)}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="text-xs text-gray-400">Line {index + 1}</span>
                              {allocatedLineNumbers.has(index + 1) && (
                                <div className="mt-1">
                                  <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}>Allocated ✓</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <select
                            value={lineItemAllocations[index] || ''}
                            onChange={(e) => setLineItemAllocations(prev => ({ ...prev, [index]: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400"
                          >
                            <option value="">Select subcontractor...</option>
                            {subbiesList.map(s => (
                              <option key={s.id} value={s.id}>{s.company_name}</option>
                            ))}
                          </select>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Pickup date</label>
                              <input
                                type="date"
                                value={lineItemDates[index]?.pickup || ''}
                                onChange={(e) => setLineItemDates(prev => ({ ...prev, [index]: { ...(prev[index] || {}), pickup: e.target.value } }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Delivery date</label>
                              <input
                                type="date"
                                value={lineItemDates[index]?.delivery || ''}
                                onChange={(e) => setLineItemDates(prev => ({ ...prev, [index]: { ...(prev[index] || {}), delivery: e.target.value } }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-400"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <p className="text-[11px] text-gray-400">Discount / negative lines aren't shown — they never apply to a subcontractor.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center sticky bottom-0 bg-white rounded-b-2xl">
              {allocateMode === 'whole' ? (
                <>
                  <span className="text-sm text-gray-500">
                    {selectedCount} subbie{selectedCount !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={handleAllocateSubbies}
                    disabled={allocating || selectedCount === 0}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {allocating ? <><FaSpinner className="animate-spin" /> Allocating...</> : <><FaUserTie size={14} /> Allocate Subcontractors</>}
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-500">
                    {(selectedJob.items || []).filter(it => (parseFloat(it.unit_price) || 0) > 0).length} allocatable line item(s)
                  </span>
                  <button
                    onClick={handleAllocateLineItems}
                    disabled={allocating || Object.keys(lineItemAllocations).filter(k => lineItemAllocations[k]).length === 0}
                    className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {allocating ? <><FaSpinner className="animate-spin" /> Allocating...</> : <><FaList size={14} /> Allocate Line Items</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Create New Job</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateJob} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client Selection */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      id="isRFQ"
                      checked={isRFQ}
                      onChange={(e) => setIsRFQ(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="isRFQ" className="text-sm font-medium text-gray-700">
                      <FaFileAlt className="inline mr-1 text-purple-500" /> This is an RFQ (Request for Quote)
                    </label>
                  </div>
                </div>

                {!isRFQ ? (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                    <select
                      value={jobForm.client_id}
                      onChange={(e) => setJobForm({ ...jobForm, client_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      required
                    >
                      <option value="">Select Client...</option>
                      {clientsList.map(c => (
                        <option key={c.id} value={c.id}>{c.company_name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="md:col-span-2 bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-700 font-medium mb-3">
                      <FaFileAlt className="inline mr-2" /> RFQ Client Details
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                        <input
                          type="text"
                          value={jobForm.rfq_client_name}
                          onChange={(e) => setJobForm({ ...jobForm, rfq_client_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                          required
                          placeholder="e.g. ABC Mining"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                        <input
                          type="text"
                          value={jobForm.rfq_contact_person}
                          onChange={(e) => setJobForm({ ...jobForm, rfq_contact_person: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <input
                          type="email"
                          value={jobForm.rfq_contact_email}
                          onChange={(e) => setJobForm({ ...jobForm, rfq_contact_email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                          required
                          placeholder="e.g. info@abcmining.co.za"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={jobForm.rfq_contact_phone}
                          onChange={(e) => setJobForm({ ...jobForm, rfq_contact_phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. +27 82 123 4567"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
                        <input
                          type="text"
                          value={jobForm.rfq_company_address}
                          onChange={(e) => setJobForm({ ...jobForm, rfq_company_address: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                          placeholder="e.g. 123 Main Street, Johannesburg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ✅ NEW: Job Type Selection */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select
                    value={jobForm.quote_type}
                    onChange={(e) => {
                      const val = e.target.value;
                      setJobForm({ ...jobForm, quote_type: val });
                      setJobType(val);
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="minerals">⛏️ Minerals</option>
                    <option value="agri">🌾 Agri</option>
                    <option value="container">📦 Containers</option>
                    <option value="abnormal">🚛 Abnormal</option>
                    <option value="general">📋 General</option>
                  </select>
                </div>

                {/* Price Type */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Type</label>
                  <select
                    value={jobForm.price_type || 'quoted'}
                    onChange={(e) => setJobForm({ ...jobForm, price_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="quoted">💰 Quoted Price (Subbie quoted this rate — fixed)</option>
                    <option value="target">🎯 Target Price (We want to hit this — subbie can negotiate)</option>
                  </select>
                </div>

                {/* ⛏️ Minerals / 🌾 Agri fields */}
                {(jobForm.quote_type === 'minerals' || jobForm.quote_type === 'agri') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity *</label>
                      <input
                        type="text"
                        value={jobForm.commodity}
                        onChange={(e) => setJobForm({ ...jobForm, commodity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. Chrome Concentrate"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity Type</label>
                      <select
                        value={jobForm.commodity_type}
                        onChange={(e) => setJobForm({ ...jobForm, commodity_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="mining">Mining</option>
                        <option value="agricultural">Agricultural</option>
                        <option value="cross_border">Cross Border</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Quantity (MT) *</label>
                      <input
                        type="number"
                        value={jobForm.total_quantity_mt}
                        onChange={(e) => setJobForm({ ...jobForm, total_quantity_mt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 15000"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rate per MT *</label>
                      <input
                        type="number"
                        value={jobForm.rate_per_mt}
                        onChange={(e) => setJobForm({ ...jobForm, rate_per_mt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 500"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">What you quote the client</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subbie Target Price *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={jobForm.subbie_target_price}
                        onChange={(e) => {
                          const subbiePrice = parseFloat(e.target.value) || 0;
                          const clientPrice = parseFloat(jobForm.admin_quote_to_client) || 0;
                          setJobForm({ ...jobForm, subbie_target_price: e.target.value });
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 440"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">What you offer the subcontractor</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client Quote (Admin) R/MT</label>
                      <input
                        type="number"
                        step="0.01"
                        value={jobForm.admin_quote_to_client}
                        onChange={(e) => {
                          const clientPrice = parseFloat(e.target.value) || 0;
                          const subbiePrice = parseFloat(jobForm.subbie_target_price) || 0;
                          setJobForm({ ...jobForm, admin_quote_to_client: e.target.value });
                        }}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg focus:outline-none focus:border-green bg-green-50"
                        placeholder="e.g. 550"
                      />
                      <p className="text-xs text-gray-400 mt-1">What the client pays you per MT</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Load Size per Truck (MT)</label>
                      <input
                        type="number"
                        step="0.5"
                        value={jobForm.load_size_per_truck}
                        onChange={(e) => setJobForm({ ...jobForm, load_size_per_truck: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 34"
                      />
                      <p className="text-xs text-gray-400 mt-1">How many MT each truck can carry</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km)</label>
                      <input
                        type="number"
                        value={jobForm.distance_km}
                        onChange={(e) => setJobForm({ ...jobForm, distance_km: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 850"
                        readOnly
                      />
                      <p className="text-xs text-gray-400 mt-1">Auto-calculated using Route Calculator below</p>
                    </div>
                    {calculateMargin() && (
                      <div className="md:col-span-2 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <FaCalculator className="text-green-600" size={20} />
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="text-green-700 font-bold">
                              Profit: {calculateMargin().percentage.toFixed(1)}%
                            </span>
                            <span className="text-green-600">
                              R{calculateMargin().profit.toFixed(2)}/MT
                            </span>
                            <span className="text-green-600">
                              Total: R{(calculateMargin().profit * parseFloat(jobForm.total_quantity_mt || 0)).toFixed(2)}
                            </span>
                            {calculateCPK() && jobForm.distance_km && (
                              <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                CPK: R{calculateCPK().toFixed(2)}/km
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* 🚛 Abnormal fields - Line items with cost pricing */}
                {jobForm.quote_type === 'abnormal' && (
                  <div className="md:col-span-2">
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                        <span className="text-sm font-medium text-navy">Line Items</span>
                        <button
                          type="button"
                          onClick={() => {
                            setJobForm(prev => ({
                              ...prev,
                              items: [...prev.items, { description: '', quantity: 1, cost_price: 0, margin_percentage: '', unit_price: 0, total: 0 }]
                            }));
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-blue-700 transition-colors"
                        >
                          <FaPlus size={12} /> Add Item
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-16">Qty</th>
                              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28 bg-blue-50 text-blue-700">Cost Price (Subbie)</th>
                              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20 bg-blue-50 text-blue-700">Margin %</th>
                              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Unit Price</th>
                              <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Total</th>
                              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-12">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobForm.items.map((item, index) => {
                              const itemQty = parseFloat(item.quantity) || 1;
                              const itemCost = parseFloat(item.cost_price) || 0;
                              const itemPrice = parseFloat(item.unit_price) || 0;
                              const itemTotal = itemQty * itemPrice;
                              const itemMarginPct = parseFloat(item.margin_percentage) || 0;
                              return (
                                <tr key={index} className="border-b border-gray-100">
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={item.description}
                                      onChange={(e) => {
                                        const newItems = [...jobForm.items];
                                        newItems[index].description = e.target.value;
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
                                      placeholder="Item description..."
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={parseInt(item.quantity) || 1}
                                      onChange={(e) => {
                                        const newItems = [...jobForm.items];
                                        const qty = parseInt(e.target.value) || 1;
                                        newItems[index].quantity = qty;
                                        const price = parseFloat(newItems[index].unit_price) || 0;
                                        newItems[index].total = qty * price;
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-green"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.cost_price || ''}
                                      onChange={(e) => {
                                        const newItems = [...jobForm.items];
                                        const cost = parseFloat(e.target.value) || 0;
                                        newItems[index].cost_price = cost;
                                        const marginPct = parseFloat(newItems[index].margin_percentage) || 0;
                                        if (marginPct > 0 && cost > 0) {
                                          const price = cost * (1 + marginPct / 100);
                                          newItems[index].unit_price = parseFloat(price.toFixed(2));
                                        }
                                        newItems[index].total = parseFloat(newItems[index].unit_price || 0) * (parseFloat(newItems[index].quantity) || 1);
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-green bg-blue-50"
                                      placeholder="0.00"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      value={item.margin_percentage || ''}
                                      onChange={(e) => {
                                        const newItems = [...jobForm.items];
                                        const marginPct = parseFloat(e.target.value) || 0;
                                        newItems[index].margin_percentage = marginPct;
                                        const cost = parseFloat(newItems[index].cost_price) || 0;
                                        if (marginPct > 0 && cost > 0) {
                                          const price = cost * (1 + marginPct / 100);
                                          newItems[index].unit_price = parseFloat(price.toFixed(2));
                                        }
                                        newItems[index].total = parseFloat(newItems[index].unit_price || 0) * (parseFloat(newItems[index].quantity) || 1);
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-green bg-blue-50"
                                      placeholder="%"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={item.unit_price || ''}
                                      onChange={(e) => {
                                        const newItems = [...jobForm.items];
                                        const price = parseFloat(e.target.value) || 0;
                                        newItems[index].unit_price = price;
                                        const cost = parseFloat(newItems[index].cost_price) || 0;
                                        if (cost > 0 && price > 0) {
                                          newItems[index].margin_percentage = parseFloat((((price - cost) / cost) * 100).toFixed(1));
                                        }
                                        newItems[index].total = price * (parseFloat(newItems[index].quantity) || 1);
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-green"
                                      placeholder="0.00"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right text-sm font-medium text-navy">
                                    R {(parseFloat(item.unit_price || 0) * (parseFloat(item.quantity) || 1)).toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = jobForm.items.filter((_, i) => i !== index);
                                        setJobForm(prev => ({ ...prev, items: newItems }));
                                      }}
                                      className="text-red-500 hover:text-red-700 transition-colors"
                                    >
                                      <FaTrash size={12} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {/* Cost totals summary */}
                      {jobForm.items.length > 0 && (
                        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex justify-between text-sm">
                          <span className="text-gray-500">
                            {jobForm.items.filter(i => i.description.trim()).length} items
                          </span>
                          <span className="font-medium text-navy">
                            Total: R {jobForm.items.reduce((sum, i) => sum + (parseFloat(i.unit_price || 0) * (parseFloat(i.quantity) || 1)), 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                    {jobForm.items.length === 0 && (
                      <p className="text-xs text-gray-400 mt-2">Add line items with cost pricing for each item</p>
                    )}
                  </div>
                )}

                {/* 📋 General fields */}
                {jobForm.quote_type === 'general' && (
                  <div className="md:col-span-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subbie Target Price *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={jobForm.subbie_target_price}
                        onChange={(e) => setJobForm({ ...jobForm, subbie_target_price: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="Total subbie price"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* ✅ NEW: Job Type Specific Fields */}
                <div className="md:col-span-2">
                  {renderJobTypeFields()}
                </div>

                {/* ✅ Route using InteractiveRouteMap */}
                <div className="md:col-span-2">
                  <InteractiveRouteMap
                    originValue={jobForm.origin}
                    destinationValue={jobForm.destination}
                    initialOriginCoords={originCoords}
                    initialDestCoords={destCoords}
                    onOriginChange={(val) => setJobForm(prev => ({ ...prev, origin: val }))}
                    onDestinationChange={(val) => setJobForm(prev => ({ ...prev, destination: val }))}
                    onOriginCoordsChange={(data) => {
                      if (data) {
                        setOriginCoords(data);
                        setJobForm(prev => ({ ...prev, origin_country: data.country || prev.origin_country }));
                        if (destCoords?.country) applyCrossBorderFromCountries(data.country, destCoords.country);
                      }
                    }}
                    onDestCoordsChange={(data) => {
                      if (data) {
                        setDestCoords(data);
                        setJobForm(prev => ({ ...prev, destination_country: data.country || prev.destination_country }));
                        if (originCoords?.country) applyCrossBorderFromCountries(originCoords.country, data.country);
                      }
                    }}
                    onDistanceCalculated={handleDistanceCalculated}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={jobForm.start_date}
                    onChange={(e) => setJobForm({ ...jobForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated End Date *</label>
                  <input
                    type="date"
                    value={jobForm.estimated_end_date}
                    onChange={(e) => setJobForm({ ...jobForm, estimated_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={jobForm.priority}
                    onChange={(e) => setJobForm({ ...jobForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={jobForm.notes}
                    onChange={(e) => setJobForm({ ...jobForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingJob}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
                >
                  {creatingJob ? 'Creating...' : <><FaPlus className="inline mr-2" size={14} /> Create Job</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {showEditModal && selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Edit Job</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateJob} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                  <select
                    value={jobForm.client_id}
                    onChange={(e) => setJobForm({ ...jobForm, client_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  >
                    <option value="">Select Client...</option>
                    {clientsList.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>

                {/* ✅ NEW: Job Type Selection in Edit */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select
                    value={jobForm.quote_type}
                    onChange={(e) => {
                      const val = e.target.value;
                      setJobForm({ ...jobForm, quote_type: val });
                      setJobType(val);
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="minerals">⛏️ Minerals</option>
                    <option value="agri">🌾 Agri</option>
                    <option value="container">📦 Containers</option>
                    <option value="abnormal">🚛 Abnormal</option>
                    <option value="general">📋 General</option>
                  </select>
                </div>

                {/* ⛏️ Minerals / 🌾 Agri fields (Edit) */}
                {(jobForm.quote_type === 'minerals' || jobForm.quote_type === 'agri') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity *</label>
                      <input
                        type="text"
                        value={jobForm.commodity}
                        onChange={(e) => setJobForm({ ...jobForm, commodity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity Type</label>
                      <select
                        value={jobForm.commodity_type}
                        onChange={(e) => setJobForm({ ...jobForm, commodity_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="mining">Mining</option>
                        <option value="agricultural">Agricultural</option>
                        <option value="cross_border">Cross Border</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Quantity (MT) *</label>
                      <input
                        type="number"
                        value={jobForm.total_quantity_mt}
                        onChange={(e) => setJobForm({ ...jobForm, total_quantity_mt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rate per MT *</label>
                      <input
                        type="number"
                        value={jobForm.rate_per_mt}
                        onChange={(e) => setJobForm({ ...jobForm, rate_per_mt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subbie Target Price *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={jobForm.subbie_target_price}
                        onChange={(e) => setJobForm({ ...jobForm, subbie_target_price: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Load Size per Truck (MT)</label>
                      <input
                        type="number"
                        step="0.5"
                        value={jobForm.load_size_per_truck}
                        onChange={(e) => setJobForm({ ...jobForm, load_size_per_truck: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        placeholder="e.g. 34"
                      />
                    </div>
                    {calculateMargin() && (
                      <div className="md:col-span-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-green-700 font-bold">
                            Profit: {calculateMargin().percentage.toFixed(1)}%
                          </span>
                          <span className="text-green-600">
                            R{calculateMargin().profit.toFixed(2)}/MT
                          </span>
                          {calculateCPK() && jobForm.distance_km && (
                            <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                              CPK: R{calculateCPK().toFixed(2)}/km
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ✅ NEW: Job Type Specific Fields in Edit */}
                <div className="md:col-span-2">
                  {renderJobTypeFields()}
                </div>

                {/* ✅ Route using InteractiveRouteMap (Edit) */}
                <div className="md:col-span-2">
                  <InteractiveRouteMap
                    originValue={jobForm.origin}
                    destinationValue={jobForm.destination}
                    initialOriginCoords={originCoords}
                    initialDestCoords={destCoords}
                    onOriginChange={(val) => setJobForm(prev => ({ ...prev, origin: val }))}
                    onDestinationChange={(val) => setJobForm(prev => ({ ...prev, destination: val }))}
                    onOriginCoordsChange={(data) => {
                      if (data) {
                        setOriginCoords(data);
                        setJobForm(prev => ({ ...prev, origin_country: data.country || prev.origin_country }));
                        if (destCoords?.country) applyCrossBorderFromCountries(data.country, destCoords.country);
                      }
                    }}
                    onDestCoordsChange={(data) => {
                      if (data) {
                        setDestCoords(data);
                        setJobForm(prev => ({ ...prev, destination_country: data.country || prev.destination_country }));
                        if (originCoords?.country) applyCrossBorderFromCountries(originCoords.country, data.country);
                      }
                    }}
                    onDistanceCalculated={handleDistanceCalculated}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={jobForm.start_date}
                    onChange={(e) => setJobForm({ ...jobForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated End Date *</label>
                  <input
                    type="date"
                    value={jobForm.estimated_end_date}
                    onChange={(e) => setJobForm({ ...jobForm, estimated_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={jobForm.priority}
                    onChange={(e) => setJobForm({ ...jobForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km)</label>
                  <input
                    type="number"
                    value={jobForm.distance_km}
                    onChange={(e) => setJobForm({ ...jobForm, distance_km: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    readOnly
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={jobForm.notes}
                    onChange={(e) => setJobForm({ ...jobForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>

              {/* 🔵 Line Items (editable — subbie rate / client rate / auto margin) */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-navy">📋 Line Items</h3>
                  <button
                    type="button"
                    onClick={() => setJobForm(prev => ({
                      ...prev,
                      items: [...(prev.items || []), { description: '', quantity: 1, cost_price: 0, margin_percentage: '', unit_price: 0, total: 0 }]
                    }))}
                    className="flex items-center gap-1 text-xs px-3 py-1 rounded-lg text-white bg-green hover:opacity-90"
                  >
                    <FaPlus size={10} /> Add Item
                  </button>
                </div>

                {(jobForm.items || []).length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
                    No line items yet. Add one to show what this job covers.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(jobForm.items || []).map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <input
                            className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded"
                            placeholder="Description"
                            value={item.description || ''}
                            onChange={(e) => {
                              const newItems = [...(jobForm.items || [])];
                              newItems[index] = { ...newItems[index], description: e.target.value };
                              setJobForm(prev => ({ ...prev, items: newItems }));
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => setJobForm(prev => ({
                              ...prev,
                              items: (prev.items || []).filter((_, i) => i !== index)
                            }))}
                            className="text-red-400 hover:text-red-600"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          <div>
                            <label className="text-[10px] text-gray-500">Qty</label>
                            <input
                              type="number" min="1"
                              className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                              value={item.quantity || 1}
                              onChange={(e) => {
                                const newItems = [...(jobForm.items || [])];
                                const qty = parseFloat(e.target.value) || 1;
                                newItems[index] = { ...newItems[index], quantity: qty, total: (parseFloat(newItems[index].unit_price) || 0) * qty };
                                setJobForm(prev => ({ ...prev, items: newItems }));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Subbie Rate</label>
                            <input
                              type="number" step="0.01"
                              className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                              value={item.cost_price || 0}
                              onChange={(e) => {
                                const newItems = [...(jobForm.items || [])];
                                const cp = parseFloat(e.target.value) || 0;
                                newItems[index] = { ...newItems[index], cost_price: cp, margin_percentage: (parseFloat(newItems[index].unit_price) || 0) - cp };
                                setJobForm(prev => ({ ...prev, items: newItems }));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Client Rate</label>
                            <input
                              type="number" step="0.01"
                              className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                              value={item.unit_price || 0}
                              onChange={(e) => {
                                const newItems = [...(jobForm.items || [])];
                                const up = parseFloat(e.target.value) || 0;
                                newItems[index] = { ...newItems[index], unit_price: up, total: (parseFloat(newItems[index].quantity) || 1) * up, margin_percentage: up - (parseFloat(newItems[index].cost_price) || 0) };
                                setJobForm(prev => ({ ...prev, items: newItems }));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Margin (R)</label>
                            <div className="px-2 py-1 text-sm text-green-600 font-medium bg-green-50 border border-green-100 rounded">
                              R {((parseFloat(item.unit_price) || 0) - (parseFloat(item.cost_price) || 0)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="text-right text-sm font-semibold text-navy pt-2">
                      Total: R {(jobForm.items || []).reduce((sum, i) => sum + ((parseFloat(i.quantity) || 1) * (parseFloat(i.unit_price) || 0)), 0).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingJob}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
                >
                  {creatingJob ? 'Updating...' : <><FaSave className="inline mr-2" size={14} /> Update Job</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && jobToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-red-600">Confirm Delete</h2>
              <button onClick={() => setShowDeleteConfirm(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete job <strong>{jobToDelete.job_number}</strong>?
                <br />
                <span className="text-sm text-red-500">This action cannot be undone.</span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteJob}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                >
                  <FaTrash className="inline mr-2" size={14} /> Delete Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Job Modal */}
      {showViewModal && viewingJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowViewModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Job Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <ProformaInvoiceButton job={viewingJob} onSuccess={fetchData} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Job Number</p>
                  <p className="font-bold text-navy">
                    {viewingJob.job_number}
                    {viewingJob.is_rfq && (
                      <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">RFQ</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Job Type</p>
                  <p className="font-bold text-navy">{getQuoteTypeLabel(viewingJob.quote_type || 'minerals')}</p>
                </div>
                {viewingJob.quote_id && (
                  <div>
                    <p className="text-sm text-gray-500">Quote Reference</p>
                    <Link
                      to={`/quotes/${viewingJob.quote_id}`}
                      className="font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      {getQuoteRefFromJob(viewingJob) || 'View Quote'} <FaArrowRight size={12} />
                    </Link>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-bold text-navy">{viewingJob.client?.company_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Origin</p>
                  <p className="font-bold text-navy">{viewingJob.origin || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Destination</p>
                  <p className="font-bold text-navy">{viewingJob.destination || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-bold text-navy">{viewingJob.start_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{viewingJob.quote_type === 'container' ? 'Containers' : 'Commodity'}</p>
                  <p className="font-bold text-navy">
                    {viewingJob.quote_type === 'container'
                      ? `${viewingJob.container_count || 1} × ${viewingJob.container_size || '6m'} ${viewingJob.container_type || 'dry'}`
                      : (viewingJob.commodity || 'N/A')}
                  </p>
                </div>
                {viewingJob.quote_type === 'container' && viewingJob.cross_border === true && (
                  <div>
                    <p className="text-sm text-gray-500">Cross-Border</p>
                    <p className="font-bold text-amber-600">🛂 Yes</p>
                  </div>
                )}
                {viewingJob.quote_type === 'container' && viewingJob.truck_type && (
                  <div>
                    <p className="text-sm text-gray-500">Truck Required</p>
                    <p className="font-bold text-navy">{String(viewingJob.truck_type).replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>

              {/* 🔵 Line Items (read-only) */}
              {viewingJob.items && viewingJob.items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-semibold text-navy mb-3">📋 Line Items</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase">
                          <th className="py-2 pr-3">Description</th>
                          <th className="py-2 pr-3">Qty</th>
                          <th className="py-2 pr-3">Subbie Rate</th>
                          <th className="py-2 pr-3">Client Rate</th>
                          <th className="py-2 pr-3">Margin (R)</th>
                          <th className="py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingJob.items.map((item, index) => (
                          <tr key={index} className="border-t border-gray-100">
                            <td className="py-2 pr-3 font-medium">{item.description || '—'}</td>
                            <td className="py-2 pr-3">{item.quantity || 1}</td>
                            <td className="py-2 pr-3">R {(parseFloat(item.cost_price) || 0).toFixed(2)}</td>
                            <td className="py-2 pr-3">R {(parseFloat(item.unit_price) || 0).toFixed(2)}</td>
                            <td className="py-2 pr-3 text-green-600">R {((parseFloat(item.unit_price) || 0) - (parseFloat(item.cost_price) || 0)).toFixed(2)}</td>
                            <td className="py-2 text-right font-semibold">R {((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!removeAllocTarget}
        onClose={() => setRemoveAllocTarget(null)}
        title="Remove Subcontractor"
        message="Are you sure you want to remove this subcontractor from the job?"
        confirmLabel="Remove"
        danger
        onConfirm={performRemoveAllocation}
      />
      {/* ✅ RECORD COMPLETED WORK MODAL */}
      {showRecordModal && recordJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRecordModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-navy">✅ Record Completed Work</h2>
                <p className="text-sm text-gray-500">{recordJob.job_number} — mark line item(s) that were done off-system</p>
              </div>
              <button onClick={() => setShowRecordModal(false)} className="text-gray-500 hover:text-navy"><FaTimes size={20} /></button>
            </div>
            <div className="p-6">
              {(lineItemsByJob[recordJob.id] || []).filter(li => li.subcontractor_id).length === 0 ? (
                <p className="text-sm text-gray-500">No assigned line items yet. Allocate lines to subcontractors first.</p>
              ) : (
                <div className="space-y-4">
                  {(lineItemsByJob[recordJob.id] || []).filter(li => li.subcontractor_id).map(li => {
                    const s = subbiesList.find(x => x.id === li.subcontractor_id);
                    const d = recordDrafts[li.id] || {};
                    const done = li.status === 'completed';
                    return (
                      <div key={li.id} className={`border rounded-xl p-4 ${done ? 'bg-green-50 border-green-200' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-navy text-sm">
                            {li.title || li.description || 'Line item'} <span className="text-gray-400">(L{li.line_number})</span>
                          </p>
                          {done ? (
                            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✅ Completed</span>
                          ) : (
                            <span className="text-xs text-gray-500">{s?.company_name || 'Subcontractor'}</span>
                          )}
                        </div>
                        {!done && (
                          <>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Started (date)</label>
                                <input type="date" value={d.started_at || ''} onChange={(e) => handleRecordDraft(li.id, 'started_at', e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Finished (date)</label>
                                <input type="date" value={d.ended_at || ''} onChange={(e) => handleRecordDraft(li.id, 'ended_at', e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Cost to subbie (ex-VAT)</label>
                                <input type="number" min="0" step="0.01" value={d.cost_override ?? ''} onChange={(e) => handleRecordDraft(li.id, 'cost_override', e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Notes (optional)</label>
                                <input type="text" value={d.notes || ''} onChange={(e) => handleRecordDraft(li.id, 'notes', e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            </div>
                            <button
                              onClick={() => submitRecordCompleted(li)}
                              disabled={recordingId === li.id}
                              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                            >
                              {recordingId === li.id ? 'Recording…' : 'Record completed work'}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


    </AdminLayout>
  );
}

export default NewJobs;